import { oembed } from '@loomhq/loom-embed'
import { InputText } from '@snowball-tech/fractal/dist/src/components/InputText'
import { TabContent as FractalTabContent } from '@snowball-tech/fractal/dist/src/components/Tabs'
import getVideoId from 'get-video-id'

import { useCallback } from 'react'

import debounce from 'lodash/debounce'
import isEmpty from 'lodash/fp/isEmpty'

import type { MediaProviderTabContentProps } from '../../../types'

import { AllowedMediaSources } from '../../../enums'
import { getFullMediaUrl, getVideoThumbnail, slugify } from '../../../helpers'
import useApi from '../../../hooks/useApi'
import useLogger from '../../../hooks/useLogger'
import useTranslations from '../../../hooks/useTranslations'

const provider = AllowedMediaSources.Link

export default function Link({
  onPick,
  setIsLoading,
  type,
}: MediaProviderTabContentProps) {
  const $t = useTranslations()

  const endpoints = useApi()

  const logger = useLogger()

  const check = async (url: string) => {
    setIsLoading?.(true)

    switch (type) {
      case 'audio': {
        const audio = new Audio()

        audio.onloadedmetadata = () => {
          onPick(url, provider, { duration: audio.duration })
        }
        audio.src = url as string

        break
      }

      case 'image': {
        const request = new XMLHttpRequest()

        request.open('GET', url, true)
        request.send()

        request.onload = () => {
          if (request.status === 200) {
            const image = new Image()

            image.onload = async () => {
              onPick(url, provider, {
                height: image.naturalHeight,
                width: image.naturalWidth,
              })
            }

            image.src = url
          }
        }

        request.onerror = () => {
          setIsLoading?.(false)
        }

        request.ontimeout = () => {
          setIsLoading?.(false)
        }

        request.onabort = () => {
          setIsLoading?.(false)
        }

        break
      }

      case 'video': {
        const { id: videoId, service } = getVideoId(url)
        const isYoutube = service === 'youtube'
        const isLoom = service === 'loom'

        if (isYoutube) {
          onPick(url, provider, {
            thumbnailUrl: `http://img.youtube.com/vi/${videoId}/0.jpg`,
          })
        } else if (isLoom) {
          try {
            const metadata = await oembed(url.replace('/embed/', '/share/'))

            onPick(url, provider, {
              thumbnailUrl: metadata.thumbnail_url ?? '',
            })
          } catch (error) {
            const actualError = error as Error

            logger.error(
              `An error occured when loading Loom video metadata: "${actualError.message}`,
              { provider, url },
              actualError,
            )

            onPick(url, provider, {}, true)
          }
        } else {
          onPick(url, provider)

          try {
            const { name: fileName, thumbnail } =
              (await getVideoThumbnail(url)) ?? {}

            if (!isEmpty(thumbnail)) {
              const [uploadedThumbnail] = await endpoints.upload.media(
                thumbnail,
                slugify(
                  `linked-video-${new Date().toISOString()}_thumbnail_${fileName ?? 'unknown_blob.jpg'}`,
                ),
              )
              const thumbnailUrl = isEmpty(uploadedThumbnail)
                ? ''
                : getFullMediaUrl(uploadedThumbnail?.path)

              onPick(url, provider, { thumbnailUrl: thumbnailUrl ?? '' }, true)
            }
          } catch (error) {
            const actualError = error as Error

            logger.error(
              `An error occurred when generating the video thumbnail: "${actualError.message}`,
              { provider, url },
              actualError,
            )

            onPick(url, provider, {}, true)
          }
        }
        break
      }

      default: {
        onPick(url, provider)
        break
      }
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheck = useCallback(
    debounce(check, 500, { leading: true }),
    [],
  )

  return (
    <FractalTabContent className="flex flex-col gap-2" name="link">
      <InputText
        fullWidth
        placeholder={$t(`editor.${type}.loader.${provider}.input.placeholder`)}
        onChange={(_event, url) => debouncedCheck(url)}
      />
    </FractalTabContent>
  )
}
